/**
 * Plan API Service
 * API calls for plan and draft operations
 */

import { STORAGE_KEYS } from '../constants/storage';
import { isCognitoConfigured } from '../config/cognito';
import { refreshAccessToken, refreshAccessTokenLocal } from './authApi';
import type { ProjectionResponse, MarketShockConfig, ShockComparisonResponse, MonteCarloConfigRequest, MonteCarloResponse, SpendingSensitivityConfigRequest, SpendingSensitivityResponse, GuardrailConfig, DeferralComparisonConfig, DeferralComparisonResponse, StrategyComparisonResponse, HistoricalReplayResponse, WhatIfScenarioConfig, WhatIfComparisonResponse, SpendingApproachConfig, BreakEvenResponse, EarliestRetirementConfig, EarliestRetirementResponse, HouseholdYearProjection } from '../types/projection';
import type { components } from '../types/api.generated';

export interface FundMetadataResponse {
  ticker: string;
  isin?: string;
  name: string;
  provider?: string;
  exchange?: string;   // display exchange, e.g. "NASDAQ", "LSE" (#1636)
  assetClass: 'EQUITY' | 'BOND' | 'MIXED' | 'COMMODITY' | 'OTHER';
  category?: string;
  ocfPct?: number;
  securityType?: string;
  source: 'YAHOO' | 'OPENFIGI' | 'MANUAL' | 'TWELVE_DATA' | 'SEED';
}

export interface SavePlanRequest {
  planId?: string;
  step: number;
  formData: Record<string, unknown>;
  name?: string;
}

export interface SavePlanResponse {
  planId: string;
  savedAt: string;
  step: number;
}

export interface PlanResponse {
  planId: string;
  step: number;
  formData: Record<string, unknown>;
  name: string;
  savedAt: string;
}

// ─── Rebaseline ("Update my plan", #1421) ─────────────────────────────────

export interface RebaselineHoldingUpdate {
  holdingId: string;
  currentValue: number;
  costBasis?: number;    // omit to carry forward unchanged (guardrail: never silently reset)
  quantity?: number;
  unitPrice?: number;
  quantityTracked?: boolean;
}

export interface RebaselinePersonUpdate {
  personIndex: number;
  lsaUsedPrePlan?: number;
  isaAllowanceUsedThisTaxYear?: number;
  priorOtherTaxableIncome?: number;
  // Wizard-style breakdown behind priorOtherTaxableIncome (HIGH fix, #1592
  // phase 2 review round 2) — kept in sync with the total so a rebaseline
  // update no longer leaves the stored breakdown stale relative to a fresh
  // total (which Step 2's own sync effect would otherwise silently revert
  // on the next visit). See UpdatePlanFlow.tsx's handleSubmit.
  priorSalaryIncome?: number;
  priorDrawdownIncome?: number;
  priorStatePensionIncome?: number;
  priorRentalIncome?: number;
  priorDividendIncome?: number;
  priorPensionContributions?: number;
}

export interface RebaselineRequest {
  planAsOfDate: string;
  holdingUpdates?: Record<string, RebaselineHoldingUpdate[]>;
  persons?: RebaselinePersonUpdate[];
  expectedUpdatedAt?: string;
}

export interface RebaselineBeforeAfterSummary {
  previousPortfolioValue: number;
  actualPortfolioValue: number;
  predictedPortfolioValue: number;
  portfolioDelta: number;
  elapsedYears: number;
  impliedAnnualReturnPct: number | null;
  assumedAnnualReturnPct: number;
  spendingSustainability: {
    previousShortfallYears: number;
    updatedShortfallYears: number;
    previousDepletionYear: number | null;
    updatedDepletionYear: number | null;
  };
}

export interface RebaselineResponse extends ProjectionResponse {
  planId: string;
  savedAt: string;
  beforeAfter: RebaselineBeforeAfterSummary | null;
}

/** `clonePlan` and `renamePlan` both return the same `{ planId, name }` shape. */
export interface PlanIdentity {
  planId: string;
  name: string;
}
export type ClonePlanResponse = PlanIdentity;
export type RenamePlanResponse = PlanIdentity;

export type PlanRole = 'primary' | 'alternative';

/** The plan card's figures — a verbatim excerpt of the projection summary. */
export interface PlanHeadline {
  /** 1-indexed PLAN year of depletion (not calendar); null = survives the horizon. */
  depletionYear: number | null;
  /** Calendar year of depletion (same year as depletionYear); null = survives. Optional on older servers. */
  depletionCalendarYear?: number | null;
  /** Nominal, end-of-horizon. */
  finalPortfolioValue: number;
  /** Nominal lifetime income tax + CGT — excludes IHT. */
  totalTaxPaid: number;
  /** The horizon, for rendering depletionYear and its null case. */
  projectionYears: number;
}

export interface PlanListItem {
  planId: string;
  step: number;
  name: string;
  savedAt: string;
  createdAt: string;
  /** Server-derived — clients render this, they never infer it (#1576/#1577). */
  role: PlanRole;
  /** Absent when the plan can't produce a projection — never render as £0. */
  headline?: PlanHeadline;
}

const API_BASE_URL = '/api';
const DEFAULT_TIMEOUT_MS = 10000; // 10 second timeout for API calls

/**
 * Fetch with timeout - prevents hanging requests
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // If the caller passes an external signal, forward its abort to our controller
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener('abort', onExternalAbort);
  // Handle already-aborted signal (event listener won't fire for past events)
  if (signal?.aborted) {
    controller.abort();
  }

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(signal?.aborted ? 'Request aborted' : 'Request timed out', { cause: error });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', onExternalAbort);
  }
}

// Promise coalescing: all concurrent 401 handlers share a single refresh attempt
let refreshPromise: Promise<string> | null = null;

export function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }
  return {
    'Content-Type': 'application/json',
  };
}

async function doRefreshAccessToken(): Promise<string> {
  if (isCognitoConfigured()) {
    // Cognito SDK handles refresh internally
    const result = await refreshAccessToken();
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, result.accessToken);
    return result.accessToken;
  }

  // Local auth fallback
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  const result = await refreshAccessTokenLocal(refreshToken);
  // Standard auth pattern: persist the access token returned by our own backend.
  // NOSONAR (typescript:S5443 — tainted data in storage): token is issued by
  // our trusted auth server and must be persisted for subsequent authenticated requests.
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, result.accessToken); // NOSONAR
  return result.accessToken;
}

/**
 * Map server-returned validation `details` array into user-facing strings.
 *
 * Server-side `formatValidationDetails` already appends the field path to the
 * message. We rebuild the suffix client-side as a defensive layer for older
 * endpoints that haven't been migrated yet — once every controller is on the
 * server-side helper this becomes a no-op for our own backend, but it keeps
 * the contract resilient.
 */
function mapErrorDetails(details: unknown[]): string[] {
  return details.map(d => {
    if (d && typeof d === 'object') {
      const obj = d as { message?: string; path?: unknown };
      const message = obj.message ?? String(d);
      // Match the exact suffix the server emits ("(field: foo.bar)") so a
      // custom Zod message like "Invalid field: foo" doesn't accidentally
      // suppress path reconstruction.
      if (Array.isArray(obj.path) && obj.path.length > 0 && !message.includes('(field: ')) {
        return `${message} (field: ${obj.path.join('.')})`;
      }
      return message;
    }
    return String(d);
  });
}

function clearAuthAndRedirect(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  globalThis.location.href = '/login';
  // Note: Don't throw here - the redirect will happen and any calling code
  // should handle this gracefully. Throwing after setting location.href
  // causes unhandled promise rejections since the throw executes before navigation.
}

/**
 * Parse a successful response body into its declared type.
 *
 * This is THE assertion point for the plan API: every typed response in this
 * module passes through here, so the unchecked step exists once rather than at
 * every call site.
 *
 * What is verified is that the body is a JSON object. That catches a `200`
 * carrying a bare JSON scalar. Note it does NOT catch HTML from a gateway or an
 * empty body — `response.json()` rejects with a SyntaxError before this check
 * runs, and that propagates to the caller unwrapped.
 *
 * Field-level shapes are not re-checked, and the coverage behind that decision
 * is partial, so be precise about it: the plan and auth envelopes
 * (ProjectionResponse, SavePlanResponse, RebaselineResponse, ClonePlanResponse,
 * RenamePlanResponse, PlanListItem) are proved against the generated OpenAPI
 * schema in `types/__tests__/generatedContract.test.ts`, with CI failing on
 * drift via `openapi:check` (server -> spec) and `api:check` (spec -> generated
 * TS). The analysis endpoints — MonteCarloResponse, ShockComparisonResponse,
 * SpendingSensitivityResponse, DeferralComparisonResponse, BreakEvenResponse
 * and the rest — have NO proof, and are asserted here on trust. Extending the
 * proof list is the cheap way to close that; each one is a single line.
 */
export async function parseJsonAs<T>(response: Response): Promise<T> {
  const body: unknown = await response.json();
  if (typeof body !== 'object' || body === null) {
    throw new Error('Malformed response from server');
  }
  return body as T;
}

/**
 * The error envelope every API route returns on a non-2xx. Naming it is what
 * lets the reads below (`message`, `error`, `details`, `code`) be checked
 * rather than asserted off an `any`.
 *
 * Always read through `?.`: the parse falls back to `null`, not `{}`, because a
 * gateway can send a literal `null` body, which parses successfully — so
 * `.catch()` never fires and a direct property read would throw a TypeError out
 * of the error-reporting path itself.
 */
export interface ApiErrorBody {
  message?: string;
  error?: string;
  details?: unknown;
  code?: string;
}

async function attemptTokenRefresh<T>(retryFn: () => Promise<Response>): Promise<T> {
  refreshPromise ??= doRefreshAccessToken().finally(() => {
    refreshPromise = null;
  });

  await refreshPromise;

  const retryResponse = await retryFn();

  if (retryResponse.status === 401) {
    clearAuthAndRedirect();
    return new Promise<T>(() => {});
  }

  if (!retryResponse.ok) {
    // Same reads as handleAuthResponse below, deliberately. This path lost the
    // server's `error` field and the `code` entirely, so any request that hit a
    // 401, refreshed and retried came back as a bare "Request failed" — and
    // callers branching on code (PLAN_CAP_EXCEEDED, CONCURRENCY) silently took
    // their generic else. The server sends `error` at 122 sites, `message` at 5.
    const error = await retryResponse.json().catch(() => null) as ApiErrorBody | null;
    const apiError = new Error(error?.message || error?.error || 'Request failed') as Error & { details?: string[]; code?: string };
    if (Array.isArray(error?.details)) {
      apiError.details = mapErrorDetails(error.details);
    }
    if (typeof error?.code === 'string') {
      apiError.code = error.code;
    }
    throw apiError;
  }

  return parseJsonAs<T>(retryResponse);
}

export async function handleAuthResponse<T>(response: Response, retryFn?: () => Promise<Response>): Promise<T> {
  if (response.status === 401 && retryFn) {
    try {
      return await attemptTokenRefresh<T>(retryFn);
    } catch (error) {
      const isAuthError = error instanceof Error && (
        error.message === 'No refresh token available' ||
        error.message === 'Token refresh failed'
      );
      if (isAuthError) {
        clearAuthAndRedirect();
        return new Promise<T>(() => {});
      }
      throw error;
    }
  }

  if (response.status === 401) {
    clearAuthAndRedirect();
    return new Promise<T>(() => {});
  }

  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiErrorBody | null;
    const errorMessage = error?.message || error?.error || 'Request failed';
    const apiError = new Error(errorMessage) as Error & { details?: string[]; code?: string };
    if (Array.isArray(error?.details)) {
      apiError.details = mapErrorDetails(error.details);
    }
    if (typeof error?.code === 'string') {
      apiError.code = error.code;
    }
    throw apiError;
  }

  return parseJsonAs<T>(response);
}

/**
 * Save or update a plan.
 *
 * URL path remains `/plans/draft` after Phase 1B's internal rename — the
 * server renamed the underlying entity but kept the API surface stable. The
 * request/response field name is `planId` (was `draftId`).
 */
export async function savePlan(data: SavePlanRequest): Promise<SavePlanResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/draft`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const response = await makeRequest();
  return handleAuthResponse<SavePlanResponse>(response, makeRequest);
}

/**
 * "Update my plan" — persist refreshed holding values (and optionally LSA/ISA
 * re-prompt answers) and return a fresh projection plus a before/after summary.
 */
export async function rebaseline(planId: string, data: RebaselineRequest): Promise<RebaselineResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/draft/${planId}/rebaseline`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const response = await makeRequest();
  return handleAuthResponse<RebaselineResponse>(response, makeRequest);
}

/**
 * Get a plan by ID.
 */
export async function getPlan(planId: string): Promise<PlanResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/draft/${planId}`, {
    headers: getAuthHeaders(),
  });

  const response = await makeRequest();
  return handleAuthResponse<PlanResponse>(response, makeRequest);
}

/**
 * List all plans for the current user (max 2 per user).
 */
export async function getUserPlans(): Promise<{ plans: PlanListItem[] }> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/drafts`, {
    headers: getAuthHeaders(),
  });

  const response = await makeRequest();
  return handleAuthResponse<{ plans: PlanListItem[] }>(response, makeRequest);
}

/**
 * Delete a plan.
 */
export async function deletePlan(planId: string): Promise<void> {
  const makeRequest = () => fetch(`${API_BASE_URL}/plans/draft/${planId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  const response = await makeRequest();

  // Handle 401 with retry (coalesce concurrent refresh attempts)
  if (response.status === 401) {
    try {
      refreshPromise ??= doRefreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      await refreshPromise;

      const retryResponse = await makeRequest();
      if (retryResponse.status === 401) {
        clearAuthAndRedirect();
        return new Promise<void>(() => {});
      }
      if (!retryResponse.ok) {
        const error = await retryResponse.json().catch(() => null) as ApiErrorBody | null;
        throw new Error(error?.message || error?.error || 'Failed to delete draft');
      }
      return;
    } catch (error) {
      const isAuthError = error instanceof Error && (
        error.message === 'No refresh token available' ||
        error.message === 'Token refresh failed'
      );
      if (isAuthError) {
        clearAuthAndRedirect();
        return new Promise<void>(() => {});
      }
      throw error;
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiErrorBody | null;
    throw new Error(error?.message || error?.error || 'Failed to delete draft');
  }
}

/**
 * Create a projection from complete wizard data and return the result directly.
 * No projection ID is returned — the result is computed synchronously and embedded in the response.
 */
export async function createProjection(formData: Record<string, unknown>, signal?: AbortSignal): Promise<ProjectionResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(formData),
  }, DEFAULT_TIMEOUT_MS, signal);

  const response = await makeRequest();
  return handleAuthResponse<ProjectionResponse>(response, makeRequest);
}

/**
 * Get projection for a plan by plan ID (computed on demand from saved draft data).
 */
export async function getProjectionForPlan(planId: string): Promise<ProjectionResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/${planId}/projection`, {
    headers: getAuthHeaders(),
  });

  const response = await makeRequest();
  return handleAuthResponse<ProjectionResponse>(response, makeRequest);
}

/** The resolved chapter-timeline summary returned by GET /plans/:planId/narrative. */
export type PlanNarrativeResponse = components['schemas']['PlanNarrativeResponse'];
export type ResolvedChapterPlan = components['schemas']['ResolvedChapterPlan'];

/**
 * Get the written summary of a saved plan. Generation may call a model, so this
 * allows a longer timeout than the default read.
 */
export async function getPlanNarrative(planId: string): Promise<PlanNarrativeResponse> {
  // First load generates the summary synchronously (Sonnet ~20-35s), so this
  // must exceed the server's AI timeout (55s); subsequent loads are cached/fast.
  const NARRATIVE_TIMEOUT_MS = 75000;
  const makeRequest = () => fetchWithTimeout(
    `${API_BASE_URL}/plans/${planId}/narrative`,
    { headers: getAuthHeaders() },
    NARRATIVE_TIMEOUT_MS,
  );

  const response = await makeRequest();
  return handleAuthResponse<PlanNarrativeResponse>(response, makeRequest);
}

/**
 * Run a shock comparison (ephemeral, no DB persistence)
 */
export async function createShockComparison(
  formData: Record<string, unknown>,
  shockConfig: MarketShockConfig,
): Promise<ShockComparisonResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/shock`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, shockConfig }),
  }, 30000); // 30s timeout for double projection

  const response = await makeRequest();
  return handleAuthResponse<ShockComparisonResponse>(response, makeRequest);
}

/**
 * Run a Monte Carlo simulation (ephemeral, no DB persistence)
 */
export async function createMonteCarloSimulation(
  formData: Record<string, unknown>,
  monteCarloConfig: MonteCarloConfigRequest,
): Promise<MonteCarloResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/montecarlo`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, monteCarloConfig }),
  }, 60000); // 60s timeout for MC (1000+ iterations)

  const response = await makeRequest();
  return handleAuthResponse<MonteCarloResponse>(response, makeRequest);
}

/**
 * Run spending sensitivity analysis (ephemeral, no DB persistence)
 */
export async function createSpendingSensitivity(
  formData: Record<string, unknown>,
  sensitivityConfig: SpendingSensitivityConfigRequest,
): Promise<SpendingSensitivityResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/spending-sensitivity`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, sensitivityConfig }),
  }, 120000); // 120s timeout for sensitivity (~10 MC batches)

  const response = await makeRequest();
  return handleAuthResponse<SpendingSensitivityResponse>(response, makeRequest);
}

/**
 * Run a deferral comparison (ephemeral, no DB persistence)
 */
export async function createDeferralComparison(
  formData: Record<string, unknown>,
  deferralConfig: DeferralComparisonConfig,
): Promise<DeferralComparisonResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/deferral`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, deferralConfig }),
  }, 30000); // 30s timeout for double projection

  const response = await makeRequest();
  return handleAuthResponse<DeferralComparisonResponse>(response, makeRequest);
}

/**
 * Run a guardrail projection (ephemeral, no DB persistence)
 */
export async function createGuardrailProjection(
  formData: Record<string, unknown>,
  guardrailConfig: GuardrailConfig,
): Promise<ProjectionResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/guardrail`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, guardrailConfig }),
  }, 30000);

  const response = await makeRequest();
  return handleAuthResponse<ProjectionResponse>(response, makeRequest);
}

/**
 * Run a spending approach projection (ephemeral, no DB persistence)
 */
export async function createSpendingApproachProjection(
  formData: Record<string, unknown>,
  spendingApproachConfig: SpendingApproachConfig,
): Promise<ProjectionResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/spending-approach`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, spendingApproachConfig }),
  }, 60000); // 60s timeout (prob guardrails runs inner MC)

  const response = await makeRequest();
  return handleAuthResponse<ProjectionResponse>(response, makeRequest);
}

/**
 * Run a projection with an allocation override (ephemeral, no DB persistence)
 */
export async function createAllocationOverrideProjection(
  formData: Record<string, unknown>,
  allocationOverridePct: number,
): Promise<ProjectionResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/allocation-override`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, allocationOverridePct }),
  }, 30000);

  const response = await makeRequest();
  return handleAuthResponse<ProjectionResponse>(response, makeRequest);
}

/**
 * Run historical replay analysis (ephemeral, no DB persistence)
 */
export async function createHistoricalReplay(
  formData: Record<string, unknown>,
): Promise<HistoricalReplayResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/historical-replay`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(formData),
  }, 120000); // 120s timeout for ~71 full projections

  const response = await makeRequest();
  return handleAuthResponse<HistoricalReplayResponse>(response, makeRequest);
}

/**
 * Run strategy comparison — all 4 strategies (ephemeral, no DB persistence)
 */
export async function createStrategyComparison(
  formData: Record<string, unknown>,
): Promise<StrategyComparisonResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/strategy-compare`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(formData),
  }, 30000); // 30s timeout for 4 projections

  const response = await makeRequest();
  return handleAuthResponse<StrategyComparisonResponse>(response, makeRequest);
}

/**
 * Run a what-if scenario comparison (ephemeral, no DB persistence)
 */
export async function createWhatIfComparison(
  formData: Record<string, unknown>,
  whatIfConfig: WhatIfScenarioConfig[],
): Promise<WhatIfComparisonResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/whatif`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, whatIfConfig }),
  }, 30000); // 30s timeout for double projection

  const response = await makeRequest();
  return handleAuthResponse<WhatIfComparisonResponse>(response, makeRequest);
}

/**
 * Clone a plan to create the user's second plan. The server enforces the
 * 2-plan-per-user cap (Phase 1C dropped comparison_group_id, so the cap is
 * now `getUserPlans().length < 2`).
 */
export async function clonePlan(sourcePlanId: string, name?: string): Promise<ClonePlanResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/draft/${sourcePlanId}/clone`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  });

  const response = await makeRequest();
  return handleAuthResponse<ClonePlanResponse>(response, makeRequest);
}

/**
 * Reset the alternative plan to match the primary's form data.
 *
 * Phase 1C removed the server-side primary/alternative concept; the client
 * tracks the pair (sessionStorage / planPairStore) and passes both IDs.
 * `:planId` URL path = the alternative; body `primaryPlanId` = the primary.
 */
export async function resetAlternativeToPrimary(
  alternativePlanId: string,
  primaryPlanId: string,
): Promise<{ planId: string; savedAt: string }> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/draft/${alternativePlanId}/reset-to-primary`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ primaryPlanId }),
  });

  const response = await makeRequest();
  return handleAuthResponse<{ planId: string; savedAt: string }>(response, makeRequest);
}

/**
 * Copy the alternative plan's form data into the primary slot — "make my
 * alternative the main plan." Mirrors resetAlternativeToPrimary in argument
 * shape: alternative via URL, primary via body.
 */
export async function promoteToPrimary(
  alternativePlanId: string,
  primaryPlanId: string,
): Promise<void> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/draft/${alternativePlanId}/promote-to-primary`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ primaryPlanId }),
  });

  const response = await makeRequest();
  await handleAuthResponse<Record<string, unknown>>(response, makeRequest);
}

/**
 * Rename a plan.
 */
export async function renamePlan(planId: string, name: string): Promise<RenamePlanResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/draft/${planId}/rename`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  });

  const response = await makeRequest();
  return handleAuthResponse<RenamePlanResponse>(response, makeRequest);
}

/**
 * Compare two plans by computing both projections in-process and returning them side by side.
 */
export async function compareProjections(planAId: string, planBId: string): Promise<{ projections: Array<{ planId: string; yearlyData: HouseholdYearProjection[]; totalTaxPaid: number; finalPortfolioValue: number; depletionYear: number | null; totalShortfall: number; shortfallYears: number }> }> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/compare`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ planAId, planBId }),
  }, 60000); // 60s for two full projections

  const response = await makeRequest();
  return handleAuthResponse<{ projections: Array<{ planId: string; yearlyData: HouseholdYearProjection[]; totalTaxPaid: number; finalPortfolioValue: number; depletionYear: number | null; totalShortfall: number; shortfallYears: number }> }>(response, makeRequest);
}

/**
 * Look up fund metadata by LSE ticker code
 */
export async function lookupFundTicker(ticker: string): Promise<FundMetadataResponse | null> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/funds/lookup/${encodeURIComponent(ticker)}`, {
    headers: getAuthHeaders(),
  });

  const response = await makeRequest();

  if (response.status === 404) return null;

  return handleAuthResponse<FundMetadataResponse>(response, makeRequest);
}

export interface LivePriceResponse {
  price: number;           // GBP per unit
  exchange?: string;       // display exchange, e.g. "NASDAQ", "LSE" (#1636)
  tradeCurrency?: string;  // ISO currency it trades in: GBP / USD / EUR
  tradePrice?: number;     // unit price in tradeCurrency (equals price when GBP)
  fxRate?: number;         // tradeCurrency → GBP (1 for GBP)
  asOf: string;
  source: 'YAHOO';
}

/**
 * Fetch a fresh unit price for a ticker — optional auto-fill suggestion for the
 * quantity/unit-price entry (#1531). Returns null on no match/fetch failure;
 * callers must fall back to manual entry, never a silent stale/zero price.
 */
export async function getLivePrice(ticker: string): Promise<LivePriceResponse | null> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/funds/price/${encodeURIComponent(ticker)}`, {
    headers: getAuthHeaders(),
  });

  const response = await makeRequest();

  if (response.status === 404) return null;

  return handleAuthResponse<LivePriceResponse>(response, makeRequest);
}

/**
 * Run break-even return analysis (ephemeral, no DB persistence)
 */
export async function createBreakEvenAnalysis(
  formData: Record<string, unknown>,
): Promise<BreakEvenResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/break-even`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(formData),
  }, 30000); // 30s timeout — up to 60 projections via binary search

  const response = await makeRequest();
  return handleAuthResponse<BreakEvenResponse>(response, makeRequest);
}

/**
 * Earliest-retirement solver (ephemeral, no DB persistence).
 * Up to 36 full projections server-side, so the same 30 s ceiling as break-even.
 */
export async function createEarliestRetirement(
  formData: Record<string, unknown>,
  earliestRetirementConfig: EarliestRetirementConfig,
): Promise<EarliestRetirementResponse> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/plans/projections/earliest-retirement`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...formData, earliestRetirementConfig }),
  }, 30000);

  const response = await makeRequest();
  return handleAuthResponse<EarliestRetirementResponse>(response, makeRequest);
}

/**
 * Search funds by name or ticker substring
 */
export async function searchFunds(query: string): Promise<FundMetadataResponse[]> {
  const makeRequest = () => fetchWithTimeout(`${API_BASE_URL}/funds/search?q=${encodeURIComponent(query)}`, {
    headers: getAuthHeaders(),
  });

  const response = await makeRequest();
  return handleAuthResponse<FundMetadataResponse[]>(response, makeRequest);
}
